import {
  GatewayContact,
  GatewayEntreprise,
  GatewayGarantieFinanciere,
} from "@conformite/gateway";
import {
  Button,
  displayErrorToast,
  Link,
  MaxWidthContent,
  SkeletonCard,
} from "@endya-conformite/ui-kit";
import { StoreEntryFetchStatus } from "@src/store/store";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { MainContentPageTemplate } from "@src/components/MainPageTemplate/MainContentPageTemplate";
import ChevronLeft from "@endya-conformite/ui-kit/src/assets/icons/chevron-left.svg?react";
import { useEntreprises } from "@src/store/store.entreprise";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { useMillesime } from "@src/store/store.millesime";
import { GatewayGarantieFinanciereApi } from "@src/api/conformite/garantieFinanciere.api";
import {
  useHasForbiddenTypeAdhesion,
  useIsAllowed,
} from "@src/session/useIsAllowed";
import styles from "./ConformiteGarantieFinanciere.module.scss";
import { ConformiteGarantieFinanciereReadIas } from "./read/ConformiteGarantieFinanciereReadIas";
import { ConformiteGarantieFinanciereIasEdit } from "./edit/ConformiteGarantieFinanciereIasEdit";

export function ConformiteGarantieFinanciereIas() {
  const { id } = useParams<{
    id: string;
  }>();
  const [query] = useSearchParams();
  const { hasAllowedRole } = useIsAllowed();
  const hasForbiddenTypeAdhesion = useHasForbiddenTypeAdhesion();
  const fromAxe = query.get("fromAxe");
  const { selected: millesime } = useMillesime();
  const { entreprisesBySiren, updateAvancement, status } = useEntreprises();
  const [isEditing, setIsEditing] = useState(false);
  const [garantieFinanciere, setGarantieFinanciere] =
    useState<GatewayGarantieFinanciere.ConformiteGarantieFinanciereIas | null>();
  const societe = id && entreprisesBySiren ? entreprisesBySiren[id] : undefined;
  const [rerender, setRerender] = useState(Math.random());
  useEffect(() => {
    setRerender(Math.random());
  }, [garantieFinanciere]);
  const retrieveGarantieFinanciere = useCallback(() => {
    if (!id || !millesime) return;
    GatewayGarantieFinanciereApi.getGarantieFinanciereIas(id, millesime)
      .then((data) => {
        setGarantieFinanciere(data);
      })
      .catch((error) => {
        handleGatewayError({
          onUnhandled: () => {
            displayErrorToast(
              "Une erreur est survenue lors de la récupération du formulaire"
            );
          },
        })(error);
      });
  }, [id, millesime]);
  useEffect(() => {
    retrieveGarantieFinanciere();
  }, [retrieveGarantieFinanciere]);

  if (!id) {
    return <Navigate to="/societes" />;
  }

  const isConforme = societe?.avancement.garantieFinanciereIas === 100;

  return (
    <MainContentPageTemplate
      title={
        <div className={styles.title}>
          <h1>Garantie financière IAS</h1>
          <h2>{societe?.raisonSociale}</h2>
        </div>
      }
      hasMillesimeNavigation={hasAllowedRole(
        GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR,
        GatewayContact.ContactRoleUtilisateur.RESPONSABLE_CONFORMITE
      )}
      actionNavigation={
        hasForbiddenTypeAdhesion([
          GatewayEntreprise.TypeAdhesion.SOCIETE_INDIVIDUELLE,
        ]) ? null : (
          <Link
            to={
              fromAxe === null
                ? `/societes/${id}`
                : `/conformite/garantie-financiere/ias`
            }
          >
            <Button variant="text" icon={<ChevronLeft />}>
              Retour
            </Button>
          </Link>
        )
      }
      millesime={millesime}
      noEntity={!societe && status === StoreEntryFetchStatus.Loaded}
    >
      {status === StoreEntryFetchStatus.Loading && (
        <MaxWidthContent size="large">
          <SkeletonCard height={500} />
        </MaxWidthContent>
      )}
      {millesime &&
        societe &&
        garantieFinanciere !== undefined &&
        !isEditing && (
          <ConformiteGarantieFinanciereReadIas
            key={rerender}
            garantieFinanciere={garantieFinanciere}
            isConforme={isConforme}
            millesime={millesime}
            siren={id}
            setToEdit={() => setIsEditing(true)}
          />
        )}
      {millesime &&
        societe &&
        garantieFinanciere !== undefined &&
        isEditing && (
          <ConformiteGarantieFinanciereIasEdit
            siren={id}
            millesime={millesime}
            isConforme={isConforme}
            garantieFinanciere={garantieFinanciere}
            stopEditing={(value, avancement) => {
              setIsEditing(false);
              if (value) retrieveGarantieFinanciere();
              if (avancement) updateAvancement(avancement);
            }}
          />
        )}
    </MainContentPageTemplate>
  );
}
